import React from 'react';
import { arrayOf, bool, func, number, oneOfType, shape, string } from 'prop-types';
import {
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from '@mui/material';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';

const MuiDropdown = ({
  anchorOrigin,
  disabled,
  error,
  helperText,
  items,
  id,
  label,
  loading,
  name,
  onChange,
  required,
  disableAutoLabelColor,
  styleProps,
  value,
  classNames,
}) => (
  <FormControl
    className={classNames}
    disabled={loading || disabled || !items.length}
    elevation={8}
    error={error}
    fullWidth
    required={required}
  >
    {loading ? (
      <CircularProgress
        size={20}
        sx={{ color: 'secondary.main', left: 12, position: 'absolute', top: 14 }}
      />
    ) : (
      <InputLabel
        disabled={loading || disabled}
        id={`${id}-label`}
        sx={
          disableAutoLabelColor
            ? undefined
            : { color: !items.length || loading ? 'text.disabled' : 'initial' }
        }
      >
        {label}
      </InputLabel>
    )}
    {!items.length && !loading && !disabled ? (
      <Tooltip placement="bottom-end" title="No options available">
        <Select
          IconComponent={!items.length && !loading ? InfoRoundedIcon : ArrowDropDownRoundedIcon}
          id={`${name}-select`}
          inputProps={{
            sx: {
              '&.MuiOutlinedInput-input': {
                paddingBottom: '12px',
                paddingTop: 2,
              },
            },
          }}
          label={label}
          labelId={`${id}-label`}
          MenuProps={{
            ...styleProps.menu,
            anchorOrigin: { ...anchorOrigin },
            slotProps: {
              ...(styleProps.menu?.slotProps || {}),
              paper: {
                ...(styleProps.menu?.slotProps?.paper || {}),
                style: {
                  ...styleProps.paper,
                  ...(styleProps.menu?.slotProps?.paper?.style || {}),
                },
              },
            },
          }}
          name={name}
          onChange={e => onChange(e.target)}
          readOnly={loading || disabled}
          value={value}
        >
          {items.map(item => (
            <MenuItem
              key={`${item.label}-${item.value}`}
              onClick={() => {}}
              sx={{ p: `6px ${2}` }}
              value={item.value}
            >
              {item.label}
            </MenuItem>
          ))}
        </Select>
      </Tooltip>
    ) : (
      <Select
        IconComponent={
          !items.length && !loading && !disabled ? InfoRoundedIcon : ArrowDropDownRoundedIcon
        }
        id={`${name}-select`}
        inputProps={{
          sx: {
            '&.MuiOutlinedInput-input': {
              paddingBottom: '12px',
              paddingTop: 2,
            },
          },
        }}
        label={label}
        labelId={`${id}-label`}
        MenuProps={{
          ...styleProps.menu,
          anchorOrigin: { ...anchorOrigin },
          slotProps: {
            ...(styleProps.menu?.slotProps || {}),
            paper: {
              ...(styleProps.menu?.slotProps?.paper || {}),
              style: {
                ...styleProps.paper,
                ...(styleProps.menu?.slotProps?.paper?.style || {}),
              },
            },
          },
        }}
        name={name}
        onChange={e => onChange(e.target)}
        readOnly={loading || disabled}
        value={value}
      >
        {items.map(item => (
          <MenuItem
            key={`${item.label}-${item.value}`}
            onClick={() => {}}
            sx={{ p: `6px ${2}` }}
            value={item.value}
          >
            {item.label}
          </MenuItem>
        ))}
      </Select>
    )}
    <FormHelperText error={error}>{helperText}</FormHelperText>
  </FormControl>
);

MuiDropdown.propTypes = {
  anchorOrigin: shape({}),
  classNames: string,
  disableAutoLabelColor: bool,
  disabled: bool,
  error: bool,
  helperText: string,
  id: string.isRequired,
  items: arrayOf(shape({})).isRequired,
  label: string,
  loading: bool,
  name: string.isRequired,
  onChange: func.isRequired,
  required: bool,
  styleProps: shape({}),
  value: oneOfType([string, number]),
};

MuiDropdown.defaultProps = {
  anchorOrigin: {
    horizontal: 'center',
    vertical: 'bottom',
  },
  classNames: '',
  disableAutoLabelColor: false,
  disabled: false,
  error: false,
  helperText: '',
  label: '',
  loading: false,
  required: false,
  styleProps: {
    menu: {},
    paper: {},
  },
  value: '',
};

export default MuiDropdown;
